import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { useDailyProfitMonthBreakdown } from '../../hooks/useDailyProfitMonthBreakdown';
import DailyProfitChart from './DailyProfitChart';

interface DailyProfitModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Daily profit by calendar day for the current month. */
export default function DailyProfitModal({ isOpen, onClose }: DailyProfitModalProps) {
  const { days, monthTotal, monthLabel, loading, error, load } = useDailyProfitMonthBreakdown();

  useEffect(() => {
    if (isOpen) {
      void load();
    }
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99998] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="daily-profit-title"
    >
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="relative w-full max-w-2xl max-h-[min(92vh,720px)] flex flex-col rounded-2xl border border-gray-200/90 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.18)] overflow-hidden"
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 border-b border-gray-100 shrink-0">
          <div className="min-w-0">
            <h2 id="daily-profit-title" className="text-base font-semibold text-gray-900">
              Daily profit
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {monthLabel ? `${monthLabel} · sales and scheduled jobs minus labour, expenses and operating costs` : 'This month'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 py-3 bg-gray-50/80 border-b border-gray-100 shrink-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            Month to date profit
          </p>
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-gray-300 mt-1" />
          ) : (
            <p className="text-2xl font-bold text-gray-900 tabular-nums tracking-tight mt-0.5">
              {formatCurrency(monthTotal)}
            </p>
          )}
          <p className="text-[11px] text-gray-500 mt-1 leading-snug">
            Each day is what is on the books: invoices issued that day plus jobs scheduled that
            day not yet invoiced. Labour includes shifts still clocked in. Operating costs are
            spread evenly over the month. Days after today are plans.
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto ios-momentum-scroll px-4 py-3">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-7 w-7 animate-spin text-gray-300" />
            </div>
          ) : error ? (
            <p className="text-sm text-red-600 text-center py-8">{error}</p>
          ) : days.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No data for this month.</p>
          ) : (
            <>
              <DailyProfitChart days={days} className="mb-4" />
              <ul className="space-y-0.5">
                {days.map((day) => (
                  <li key={day.date}>
                    <div
                      className={`flex items-center justify-between gap-3 px-3 py-2 rounded-xl text-sm ${
                        day.isToday
                          ? 'bg-blue-50/90 ring-1 ring-blue-100'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="min-w-0">
                        <p
                          className={`font-medium ${
                            day.isToday ? 'text-blue-900' : 'text-gray-800'
                          }`}
                        >
                          {day.label}
                          {day.isToday ? (
                            <span className="ml-1.5 text-[10px] font-semibold uppercase text-blue-600">
                              Today
                            </span>
                          ) : null}
                        </p>
                        <p className="text-[11px] text-gray-500 tabular-nums truncate">
                          Sales {formatCurrency(day.revenue - (day.scheduled ?? 0))}
                          {day.scheduled ? ` · Scheduled ${formatCurrency(day.scheduled)}` : ''} · Labour{' '}
                          {formatCurrency(day.labor)} · Expenses {formatCurrency(day.directExpenses)} ·
                          Operating {formatCurrency(day.overheadExpenses)}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 tabular-nums font-semibold ${
                          day.profit >= 0 ? 'text-emerald-700' : 'text-red-600'
                        }`}
                      >
                        {formatCurrency(day.profit)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
